import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Edit, Wrench, ExternalLink, Package, DollarSign, Clock,
  Loader2, Globe, ArrowRight, Sparkles, Upload, Image as ImageIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "espresso", label: "Espresso Machines" },
  { value: "grinder", label: "Grinders" },
  { value: "brewing", label: "Brewing Equipment" },
  { value: "refrigeration", label: "Refrigeration" },
  { value: "furniture", label: "Furniture & Fixtures" },
  { value: "pos", label: "POS & Technology" },
  { value: "smallwares", label: "Smallwares" },
  { value: "signage", label: "Signage & Branding" },
  { value: "plumbing", label: "Plumbing & Electrical" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "needed", label: "Needed", color: "bg-amber-100 text-amber-700" },
  { value: "researching", label: "Researching", color: "bg-blue-100 text-blue-700" },
  { value: "ordered", label: "Ordered", color: "bg-violet-100 text-violet-700" },
  { value: "shipped", label: "Shipped", color: "bg-cyan-100 text-cyan-700" },
  { value: "received", label: "Received", color: "bg-emerald-100 text-emerald-700" },
  { value: "installed", label: "Installed", color: "bg-stone-200 text-stone-700" },
];

const emptyForm = {
  name: "", category: "other", url: "", brand: "", model_name: "",
  specs: {}, lead_time_days: "", cost: "", quantity: 1,
  status: "needed", vendor: "", notes: "", store_id: null,
  spec_dimensions: "", spec_weight: "", spec_power: "", spec_capacity: "",
};

export default function EquipmentManager() {
  const [equipment, setEquipment] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState("all");

  // URL scan state
  const [step, setStep] = useState("url"); // "url" | "form"
  const [scanUrl, setScanUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchEquipment(); }, [filterStatus]);

  const fetchData = async () => {
    try {
      const [eqRes, stRes] = await Promise.all([
        axios.get(`${API}/equipment/master`),  // Only fetch master catalog
        axios.get(`${API}/stores`),
      ]);
      setEquipment(eqRes.data);
      setStores(stRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEquipment = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      // Always fetch master equipment only for this admin page
      const res = await axios.get(`${API}/equipment/master`, { params });
      setEquipment(res.data);
    } catch (e) { console.error(e); }
  };

  const scanProductUrl = async () => {
    if (!scanUrl.trim()) { toast.error("Enter a URL first"); return; }
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/equipment/scan-url`, { url: scanUrl.trim() });
      const r = res.data;
      setScanResult(r);
      // Auto-fill the form
      setForm({
        name: r.name || "",
        category: r.category || "other",
        url: r.url || scanUrl.trim(),
        brand: r.brand || "",
        model_name: r.model_name || "",
        spec_dimensions: r.specs?.dimensions || "",
        spec_weight: r.specs?.weight || "",
        spec_power: r.specs?.power || "",
        spec_capacity: r.specs?.capacity || "",
        lead_time_days: r.lead_time_days?.toString() || "",
        cost: r.cost?.toString() || "",
        quantity: 1,
        status: "needed",
        vendor: r.vendor || "",
        notes: r.description || "",
        store_id: null,  // Master equipment
      });
      setStep("form");
      toast.success("Product details extracted!");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to scan URL";
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Equipment name is required"); return; }

    const specs = {};
    if (form.spec_dimensions) specs.dimensions = form.spec_dimensions;
    if (form.spec_weight) specs.weight = form.spec_weight;
    if (form.spec_power) specs.power = form.spec_power;
    if (form.spec_capacity) specs.capacity = form.spec_capacity;

    const payload = {
      name: form.name, category: form.category, url: form.url,
      brand: form.brand, model_name: form.model_name, specs,
      lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days) : null,
      cost: form.cost ? parseFloat(form.cost) : null,
      quantity: parseInt(form.quantity) || 1,
      status: form.status, vendor: form.vendor, notes: form.notes,
      store_id: null,  // Master equipment has no store_id
      is_master: true, // This is a master catalog item
    };

    try {
      if (editing) {
        await axios.put(`${API}/equipment/${editing.id}`, payload);
        toast.success("Equipment updated");
      } else {
        await axios.post(`${API}/equipment`, payload);
        toast.success("Equipment added to catalog");
      }
      closeDialog();
      fetchEquipment();
    } catch (e) { toast.error("Failed to save equipment"); }
  };

  const deleteEquipment = async (id) => {
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const uploadEquipmentImage = async (equipId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/equipment/${equipId}/image`, fd);
      toast.success("Image uploaded");
      fetchEquipment();
    } catch (e) { toast.error("Upload failed"); }
  };

  const deleteEquipmentImage = async (equipId, imageId) => {
    try {
      await axios.delete(`${API}/equipment/${equipId}/image/${imageId}`);
      toast.success("Image deleted");
      fetchEquipment();
    } catch (e) { toast.error("Failed"); }
  };

  const openEdit = (eq) => {
    setEditing(eq);
    setForm({
      name: eq.name, category: eq.category, url: eq.url || "",
      brand: eq.brand || "", model_name: eq.model_name || "",
      spec_dimensions: eq.specs?.dimensions || "", spec_weight: eq.specs?.weight || "",
      spec_power: eq.specs?.power || "", spec_capacity: eq.specs?.capacity || "",
      lead_time_days: eq.lead_time_days?.toString() || "", cost: eq.cost?.toString() || "",
      quantity: eq.quantity || 1, status: eq.status || "needed",
      vendor: eq.vendor || "", notes: eq.notes || "",
      store_id: eq.store_id || "none",
    });
    setStep("form");
    setScanUrl(eq.url || "");
    setScanResult(null);
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, store_id: null });
    setStep("url");
    setScanUrl("");
    setScanResult(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(emptyForm);
    setEditing(null);
    setStep("url");
    setScanUrl("");
    setScanResult(null);
  };

  const skipToManual = () => {
    setForm({ ...emptyForm, store_id: null });
    setStep("form");
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  const totalCost = equipment.reduce((sum, eq) => sum + (eq.cost || 0) * (eq.quantity || 1), 0);

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="equipment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Wrench className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Admin</span>
          </div>
          <h1 data-testid="equipment-title" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Equipment Catalog
          </h1>
          <p className="text-sm sm:text-base text-stone-500 mt-2">Master list of all equipment used across your stores. Assign items to individual stores from their detail pages.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-equipment-btn" onClick={openNew} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 font-medium px-6 btn-scale w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" /> Add Equipment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                {editing ? "Edit Equipment" : step === "url" ? "Add Equipment" : "Equipment Details"}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: URL Input */}
            {step === "url" && !editing && (
              <div className="space-y-4 mt-4">
                <div className="bg-stone-50 border border-stone-200/60 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-amber-600" />
                    <p className="text-sm font-medium text-stone-700">Paste a product URL</p>
                  </div>
                  <p className="text-xs text-stone-500 mb-3">
                    Paste a link to the equipment and we'll automatically extract the name, brand, specs, pricing, and more.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="scan-url-input"
                      value={scanUrl}
                      onChange={(e) => setScanUrl(e.target.value)}
                      placeholder="https://www.lamarzocco.com/linea-pb"
                      className="flex-1"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanProductUrl(); } }}
                    />
                    <Button
                      data-testid="scan-url-btn"
                      onClick={scanProductUrl}
                      disabled={scanning || !scanUrl.trim()}
                      className="bg-stone-900 text-stone-50 hover:bg-stone-800 px-4"
                    >
                      {scanning ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1.5" /> Scan
                        </>
                      )}
                    </Button>
                  </div>
                  {scanning && (
                    <div className="flex items-center gap-2 mt-3 text-xs text-stone-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Scanning page and extracting product details...
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-xs text-stone-400 uppercase tracking-wide">or</span>
                  </div>
                </div>

                <Button
                  data-testid="skip-to-manual-btn"
                  onClick={skipToManual}
                  variant="outline"
                  className="w-full border-stone-200 text-stone-600 hover:bg-stone-50"
                >
                  Enter details manually <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Equipment Form */}
            {step === "form" && (
              <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                {scanResult && !editing && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700 flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                    Auto-filled from URL scan. Review and adjust as needed.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs font-medium text-stone-600">URL</Label>
                    <Input data-testid="eq-url" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-medium text-stone-600">Name *</Label>
                    <Input data-testid="eq-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., La Marzocco Linea PB" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                      <SelectTrigger data-testid="eq-category" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Brand</Label>
                    <Input data-testid="eq-brand" value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="La Marzocco" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Model</Label>
                    <Input data-testid="eq-model" value={form.model_name} onChange={e => setForm({...form, model_name: e.target.value})} placeholder="Linea PB" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Cost ($)</Label>
                    <Input data-testid="eq-cost" type="number" step="0.01" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} placeholder="12500" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Quantity</Label>
                    <Input data-testid="eq-qty" type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Lead Time (days)</Label>
                    <Input data-testid="eq-leadtime" type="number" value={form.lead_time_days} onChange={e => setForm({...form, lead_time_days: e.target.value})} placeholder="45" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                      <SelectTrigger data-testid="eq-status" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Vendor</Label>
                  <Input data-testid="eq-vendor" value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="Vendor name" className="mt-1" />
                </div>
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wide pt-2">Specs</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-500">Dimensions</Label>
                    <Input data-testid="eq-dim" value={form.spec_dimensions} onChange={e => setForm({...form, spec_dimensions: e.target.value})} placeholder='35"W x 21"D x 17"H' className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Weight</Label>
                    <Input data-testid="eq-weight" value={form.spec_weight} onChange={e => setForm({...form, spec_weight: e.target.value})} placeholder="152 lbs" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Power</Label>
                    <Input data-testid="eq-power" value={form.spec_power} onChange={e => setForm({...form, spec_power: e.target.value})} placeholder="220V / 30A" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Capacity</Label>
                    <Input data-testid="eq-cap" value={form.spec_capacity} onChange={e => setForm({...form, spec_capacity: e.target.value})} placeholder="2-group" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Notes</Label>
                  <textarea data-testid="eq-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Additional notes..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400" />
                </div>
                <Button type="submit" data-testid="eq-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium mt-2">
                  {editing ? "Update" : "Add Equipment"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & Summary */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-eq-status" className="w-40 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-4 text-sm text-stone-500">
          <span className="flex items-center gap-1.5"><Package className="w-4 h-4" /> {equipment.length} items</span>
          <span className="flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> ${totalCost.toLocaleString()}</span>
        </div>
      </div>

      {/* Equipment List */}
      {equipment.length === 0 && !loading ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No equipment yet</h3>
          <p className="text-sm text-stone-500 mb-4">Paste a product URL and we'll pull in all the details for you.</p>
          <Button data-testid="first-equipment-btn" onClick={openNew} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-2" /> Add Equipment
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {equipment.map((eq, idx) => (
            <Card key={eq.id} data-testid={`equipment-card-${eq.id}`}
              className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up"
              style={{ animationDelay: `${idx * 0.04}s` }}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Wrench className="w-4 h-4 text-stone-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <h3 className="font-semibold text-stone-900 text-base">{eq.name}</h3>
                        <p className="text-xs text-stone-500">
                          {eq.brand && `${eq.brand} `}{eq.model_name && `${eq.model_name} · `}
                          {CATEGORIES.find(c => c.value === eq.category)?.label || eq.category}
                          {eq.quantity > 1 && ` · Qty: ${eq.quantity}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(eq.status)}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-stone-600 flex-wrap">
                      {eq.cost != null && (
                        <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-stone-400" /> ${(eq.cost * (eq.quantity || 1)).toLocaleString()}</span>
                      )}
                      {eq.lead_time_days != null && (
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-stone-400" /> {eq.lead_time_days} day lead</span>
                      )}
                      {eq.vendor && <span className="text-stone-400">via {eq.vendor}</span>}
                      {eq.url && (
                        <a href={eq.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-amber-700 hover:text-amber-600" onClick={e => e.stopPropagation()}>
                          <ExternalLink className="w-3.5 h-3.5" /> Link
                        </a>
                      )}
                    </div>
                    {Object.keys(eq.specs || {}).length > 0 && (
                      <div className="flex gap-3 mt-2 text-xs text-stone-500 flex-wrap">
                        {Object.entries(eq.specs).map(([k, v]) => (
                          <span key={k} className="bg-stone-50 px-2 py-0.5 rounded">{k}: {v}</span>
                        ))}
                      </div>
                    )}
                    {/* Equipment Images */}
                    <div className="flex items-center gap-2 mt-3">
                      {(eq.images || []).map(img => (
                        <div key={img.id} className="relative group/img w-12 h-12 rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
                          <img src={`${img.url}`} alt="" className="w-full h-full object-cover" />
                          <button onClick={(e) => { e.stopPropagation(); deleteEquipmentImage(eq.id, img.id); }}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                      <label data-testid={`upload-eq-img-${eq.id}`}
                        className="w-12 h-12 rounded-lg border-2 border-dashed border-stone-200 hover:border-stone-400 flex items-center justify-center cursor-pointer transition-colors">
                        <Upload className="w-4 h-4 text-stone-400" />
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadEquipmentImage(eq.id, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button data-testid={`edit-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => openEdit(eq)} className="text-stone-400 hover:text-stone-700 px-2">
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => deleteEquipment(eq.id)} className="text-stone-400 hover:text-red-600 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
